"use client";

import { useMemo } from "react";
import {
  CopyButton,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { MoonDisc } from "@/components/tools/astronomy/moon-disc";
import type { MoonPhaseStrings } from "@/components/tools/moon-phase-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { formatDateTime } from "@/lib/format-number";
import { t } from "@/lib/i18n/interpolate";
import { longDay } from "@/lib/tool-kit/astronomy/format";
import {
  moonState,
  nextPhases,
  phaseName,
  SYNODIC_MONTH,
  surroundingNewMoons,
} from "@/lib/tool-kit/astronomy/moon";
import { monthCalendar } from "@/lib/tool-kit/astronomy/moon-calendar";
import { dayNumber } from "@/lib/tool-kit/astronomy/zone";

interface Props {
  day: { year: number; month: number; day: number };
  instant: number;
  locale: string;
  southern: boolean;
  strings: MoonPhaseStrings;
  zone: string;
}

const MS_PER_DAY = 86_400_000;

function whenText(locale: string, ms: number, zone: string): string {
  return formatDateTime(locale, new Date(Math.round(ms / 60_000) * 60_000), {
    timeZone: zone,
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function Calendar({ props }: { props: Props }) {
  const { day, locale, southern, strings, zone } = props;
  const fmt = useToolNumber();
  const calendar = useMemo(
    () => monthCalendar(day.year, day.month, zone),
    [day.year, day.month, zone]
  );
  const names = strings.phases as Record<string, string>;
  const weekdays = [0, 1, 2, 3, 4, 5, 6].map((i) =>
    formatDateTime(locale, new Date(Date.UTC(2024, 0, 1 + i)), {
      weekday: "short",
      timeZone: "UTC",
    })
  );
  type Day = (typeof calendar.days)[number];
  interface Slot {
    key: string;
    cell: Day | null;
  }
  const slots: Slot[] = [
    ...Array.from({ length: calendar.leadingBlanks }, (): Day | null => null),
    ...calendar.days,
  ].map((cell, position) => ({
    key: cell ? `d${cell.day}` : `b${position}`,
    cell,
  }));
  while (slots.length % 7 !== 0) {
    slots.push({ key: `e${slots.length}`, cell: null });
  }
  const weeks: Slot[][] = [];
  for (let i = 0; i < slots.length; i += 7) {
    weeks.push(slots.slice(i, i + 7));
  }
  const monthName = formatDateTime(
    locale,
    new Date(Date.UTC(day.year, day.month - 1, 1)),
    { month: "long", year: "numeric", timeZone: "UTC" }
  );
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>
        {t(strings.calendarHeading, { month: monthName })}
      </h3>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full table-fixed border-collapse text-center text-xs">
          <thead>
            <tr>
              {weekdays.map((name) => (
                <th className="px-1 py-2 font-medium" key={name} scope="col">
                  {name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week) => (
              <tr key={week[0].key}>
                {week.map(({ key, cell }) =>
                  cell ? (
                    <td
                      className={`border-border border-t p-1 align-top ${
                        cell.day === day.day ? "bg-primary/10" : ""
                      }`}
                      key={key}
                    >
                      <div className="font-medium text-foreground">
                        {fmt(cell.day)}
                      </div>
                      <div className="flex justify-center py-1">
                        <MoonDisc
                          illumination={cell.illumination}
                          size={28}
                          southern={southern}
                          waxing={cell.waxing}
                        />
                      </div>
                      <div className="text-muted-foreground">
                        {t(strings.illuminationShort, {
                          percent: fmt(Math.round(cell.illumination * 100)),
                        })}
                      </div>
                      {cell.event ? (
                        <div className="font-medium text-primary">
                          {names[cell.event.kind] ?? cell.event.kind}
                        </div>
                      ) : null}
                    </td>
                  ) : (
                    <td className="border-border border-t" key={key} />
                  )
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-muted-foreground text-xs">{strings.calendarNote}</p>
    </div>
  );
}

export function MoonPhaseResults(props: Props) {
  const { day, instant, locale, southern, strings, zone } = props;
  const fmt = useToolNumber();
  const state = moonState(instant);
  const names = strings.phases as Record<string, string>;
  const name = names[phaseName(state.elongation)] ?? "";
  const newMoons = surroundingNewMoons(instant);
  const age = (instant - newMoons.previous) / MS_PER_DAY;
  const upcoming = nextPhases(instant, 4);
  const percent = fmt(state.illumination * 100, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const ageText = fmt(age, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const dateText = longDay(locale, dayNumber(day.year, day.month, day.day));
  return (
    <>
      <section className="space-y-4">
        <h2 className={WIDGET_H2}>{t(strings.results, { date: dateText })}</h2>
        <div className="flex flex-wrap items-center gap-6">
          <MoonDisc
            illumination={state.illumination}
            size={120}
            southern={southern}
            waxing={state.waxing}
          />
          <div className="space-y-1">
            <p className="font-semibold text-2xl text-foreground">{name}</p>
            <p className="text-foreground text-sm">
              {state.waxing ? strings.waxing : strings.waning}
            </p>
            <p className="text-muted-foreground text-sm">
              {t(strings.sinceNew, {
                when: whenText(locale, newMoons.previous, zone),
              })}
            </p>
          </div>
        </div>
        <StatGrid>
          <StatCard
            emphasis
            label={strings.illumination}
            value={`${percent}%`}
          />
          <StatCard
            label={strings.age}
            value={t(strings.ageValue, { days: ageText })}
          />
          <StatCard
            label={strings.cycle}
            value={`${fmt((age / SYNODIC_MONTH) * 100, { maximumFractionDigits: 0 })}%`}
          />
          <StatCard
            label={strings.distance}
            value={t(strings.distanceValue, {
              km: fmt(Math.round(state.distanceKm / 10) * 10),
            })}
          />
          <StatCard
            label={strings.elongation}
            value={t(strings.degrees, {
              value: fmt(state.elongation, { maximumFractionDigits: 1 }),
            })}
          />
        </StatGrid>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          text={t(strings.summary, {
            date: dateText,
            phase: name,
            percent,
            days: ageText,
          })}
        />
      </section>
      <section className={`${CARD} space-y-3`}>
        <h3 className={WIDGET_H3}>{strings.upcomingHeading}</h3>
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className={TABLE}>
            <thead>
              <tr>
                <th className={TH} scope="col">
                  {strings.phaseColumn}
                </th>
                <th className={TH} scope="col">
                  {strings.whenColumn}
                </th>
                <th className={TH} scope="col">
                  {strings.inColumn}
                </th>
              </tr>
            </thead>
            <tbody>
              {upcoming.map((event) => (
                <tr key={event.time}>
                  <td className={TD}>{names[event.kind] ?? event.kind}</td>
                  <td className={TD}>{whenText(locale, event.time, zone)}</td>
                  <td className={TD}>
                    {t(strings.inDays, {
                      days: fmt((event.time - instant) / MS_PER_DAY, {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1,
                      }),
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className={`${CARD} space-y-3`}>
        <Calendar props={props} />
      </section>
    </>
  );
}
